import React from 'react';
import { FaGraduationCap, FaBookOpen, FaUsers, FaClock, FaTrophy, FaChartLine } from 'react-icons/fa';
import '../Styles/Coaching.css';
import { Helmet } from 'react-helmet';
import Reveal, { RevealGroup, RevealItem } from './Reveal';
import MagneticButton from './MagneticButton';

export default function Coaching() {
  return (
    <div className="coaching-page">
      <Helmet>
        <title>IELTS, TOEFL & PTE Coaching Classes | Achieve Band 8.0+</title>
        <meta name="description" content="Master your English tests with expert coaching. Enjoy 95% success rates, mock tests, individual feedback, and band score guarantees. Sign up for updates!" />
        <link rel="canonical" href="https://veabroad.com/coaching" />
        <meta property="og:title" content="IELTS, TOEFL & PTE Coaching Classes | Achieve Band 8.0+" />
        <meta property="og:description" content="Master your English tests with expert coaching. Enjoy 95% success rates, mock tests, individual feedback, and band score guarantees. Sign up for updates!" />
        <meta property="og:url" content="https://veabroad.com/coaching" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veabroad.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="IELTS, TOEFL & PTE Coaching Classes | Achieve Band 8.0+" />
        <meta name="twitter:description" content="Master your English tests with expert coaching. Enjoy 95% success rates, mock tests, individual feedback, and band score guarantees. Sign up for updates!" />
        <meta name="twitter:image" content="https://veabroad.com/og-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "IELTS & English Language Preparation Program",
            "description": "Comprehensive preparation courses for IELTS, TOEFL, and PTE academic and general tests.",
            "provider": {
              "@type": "EducationalOrganization",
              "name": "Vidya Education Abroad",
              "url": "https://veabroad.com/"
            }
          })}
        </script>
      </Helmet>
      {/* HERO */}
      <section className="coaching-hero">
        <div className="va-blob-field" aria-hidden="true">
          <div className="va-blob va-blob-teal" style={{ top: "-10%", left: "10%", width: "340px", height: "340px" }}></div>
          <div className="va-blob va-blob-mint" style={{ top: "40%", left: "75%", width: "300px", height: "300px" }}></div>
          <div className="va-blob va-blob-navy" style={{ top: "60%", left: "30%", width: "260px", height: "260px" }}></div>
        </div>
        <div className="container">
          <Reveal as="div" direction="up" className="hero-content">
            <h1>IELTS &amp; <span className="va-shine-text va-shine-text-on-dark">English Language</span> Coaching</h1>
            <p>
              Achieve your target IELTS score with our expert coaching programs.
              Personalized training to help you succeed in your study abroad journey.
            </p>
            <RevealGroup as="div" className="hero-stats" stagger={0.12}>
              <RevealItem as="div" direction="zoom" className="stat">
                <span className="stat-number va-pop">95%</span>
                <span className="stat-label">Success Rate</span>
              </RevealItem>
              <RevealItem as="div" direction="zoom" className="stat">
                <span className="stat-number va-pop">500+</span>
                <span className="stat-label">Students Trained</span>
              </RevealItem>
              <RevealItem as="div" direction="zoom" className="stat">
                <span className="stat-number va-pop">8.5</span>
                <span className="stat-label">Average Band Score</span>
              </RevealItem>
            </RevealGroup>
            <MagneticButton as="button" className="lmbtn hero-btn va-shimmer-btn">
              Enroll Now - Coming Soon
            </MagneticButton>
          </Reveal>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="section">
        <div className="container">
          <Reveal as="h2" direction="up">Our Coaching Programs</Reveal>
          <RevealGroup as="div" className="programs-grid" stagger={0.1}>
            <RevealItem as="div" className="program-card va-hover-lift">
              <div className="program-icon">
                <FaGraduationCap />
              </div>
              <h3>IELTS Preparation</h3>
              <p>Comprehensive IELTS training covering all four modules: Listening, Reading, Writing, and Speaking.</p>
              <ul className="program-features">
                <li>Mock tests and practice sessions</li>
                <li>Individual feedback and improvement plans</li>
                <li>Exam strategies and time management</li>
                <li>Band score guarantee programs</li>
              </ul>
              <div className="program-duration">Duration: 6-8 weeks</div>
            </RevealItem>

            <RevealItem as="div" className="program-card va-hover-lift">
              <div className="program-icon">
                <FaBookOpen />
              </div>
              <h3>General English</h3>
              <p>Build strong English foundation with our general English courses for everyday communication.</p>
              <ul className="program-features">
                <li>Grammar and vocabulary building</li>
                <li>Conversation practice sessions</li>
                <li>Business English modules</li>
                <li>Pronunciation improvement</li>
              </ul>
              <div className="program-duration">Duration: 4-12 weeks</div>
            </RevealItem>

            <RevealItem as="div" className="program-card va-hover-lift">
              <div className="program-icon">
                <FaUsers />
              </div>
              <h3>Group Classes</h3>
              <p>Interactive group sessions with peer learning and collaborative practice opportunities.</p>
              <ul className="program-features">
                <li>Small batch sizes (8-12 students)</li>
                <li>Interactive learning environment</li>
                <li>Group discussions and debates</li>
                <li>Affordable pricing options</li>
              </ul>
              <div className="program-duration">Duration: Flexible</div>
            </RevealItem>

            <RevealItem as="div" className="program-card va-hover-lift">
              <div className="program-icon">
                <FaClock />
              </div>
              <h3>One-on-One Coaching</h3>
              <p>Personalized coaching sessions tailored to your specific needs and learning pace.</p>
              <ul className="program-features">
                <li>Customized study plans</li>
                <li>Flexible scheduling</li>
                <li>Intensive focus on weak areas</li>
                <li>Rapid improvement guarantee</li>
              </ul>
              <div className="program-duration">Duration: As needed</div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section light-bg">
        <div className="container">
          <Reveal as="h2" direction="up">Why Choose Our Coaching?</Reveal>
          <RevealGroup as="div" className="features-grid" stagger={0.1}>
            <RevealItem as="div" className="feature-card va-hover-lift">
              <div className="feature-icon">
                <FaTrophy />
              </div>
              <h3>Expert Instructors</h3>
              <p>Learn from certified IELTS trainers with years of teaching experience and proven track records.</p>
            </RevealItem>
            <RevealItem as="div" className="feature-card va-hover-lift">
              <div className="feature-icon">
                <FaChartLine />
              </div>
              <h3>Proven Results</h3>
              <p>95% of our students achieve their target band scores within the first attempt.</p>
            </RevealItem>
            <RevealItem as="div" className="feature-card va-hover-lift">
              <div className="feature-icon">
                <FaBookOpen />
              </div>
              <h3>Diverse Materials</h3>
              <p>Access to latest IELTS preparation books, online resources, and practice tests.</p>
            </RevealItem>
            <RevealItem as="div" className="feature-card va-hover-lift">
              <div className="feature-icon">
                <FaUsers />
              </div>
              <h3>Small Batch Sizes</h3>
              <p>Limited students per batch ensuring personalized attention and better learning outcomes.</p>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testimonials-section">
        <div className="container">
          <Reveal as="h2" direction="up">Student Success Stories</Reveal>
          <RevealGroup as="div" className="testimonials-grid" stagger={0.12}>
            <RevealItem as="div" className="testimonial-card va-hover-lift">
              <div className="testimonial-content">
                <p>"The coaching helped me improve from 6.0 to 8.5 in just 6 weeks. The instructors were amazing!"</p>
              </div>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>Priya Sharma</h4>
                  <span>IELTS Score: 8.5</span>
                </div>
                <div className="rating">⭐⭐⭐⭐⭐</div>
              </div>
            </RevealItem>
            <RevealItem as="div" className="testimonial-card va-hover-lift">
              <div className="testimonial-content">
                <p>"Excellent teaching methods and personalized feedback. Achieved my target score for Canada PR."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>Rahul Patel</h4>
                  <span>IELTS Score: 8.0</span>
                </div>
                <div className="rating">⭐⭐⭐⭐⭐</div>
              </div>
            </RevealItem>
            <RevealItem as="div" className="testimonial-card va-hover-lift">
              <div className="testimonial-content">
                <p>"The mock tests and speaking practice sessions were incredibly helpful for my preparation."</p>
              </div>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>Anjali Singh</h4>
                  <span>IELTS Score: 7.5</span>
                </div>
                <div className="rating">⭐⭐⭐⭐⭐</div>
              </div>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* COMING SOON */}
      <section className="coming-soon-section position-relative">
        <div className="va-blob-field" aria-hidden="true">
          <div className="va-blob va-blob-mint" style={{ top: "10%", left: "85%", width: "260px", height: "260px" }}></div>
          <div className="va-blob va-blob-teal" style={{ top: "60%", left: "5%", width: "220px", height: "220px" }}></div>
        </div>
        <div className="container position-relative">
          <Reveal as="div" direction="zoom" className="coming-soon-content">
            <h2>🚀 Launching <span className="va-shine-text va-shine-text-on-dark">Soon!</span></h2>
            <p>Our comprehensive IELTS coaching program is currently under development. We're working hard to bring you the best learning experience.</p>
            <RevealGroup as="div" className="coming-soon-features" stagger={0.1}>
              <RevealItem as="div" direction="up" className="feature-item va-hover-lift">📚 Updated curriculum based on latest IELTS format</RevealItem>
              <RevealItem as="div" direction="up" className="feature-item va-hover-lift">👨‍🏫 Certified and experienced instructors</RevealItem>
              <RevealItem as="div" direction="up" className="feature-item va-hover-lift">📊 Regular progress tracking and feedback</RevealItem>
            </RevealGroup>
            <Reveal as="div" direction="up" delay={0.15} className="notify-section">
              <h3>Get Notified When We Launch</h3>
              <p>Be the first to know when our coaching programs are available!</p>
              <MagneticButton as="button" className="lmbtn notify-btn va-shimmer-btn">
                Notify Me - Coming Soon
              </MagneticButton>
            </Reveal>
          </Reveal>
        </div>
      </section>
    </div>
  );
}